import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SignUp } from '@stackframe/react';
import { Activity, Target, TrendingUp } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      if (isAdmin) {
        navigate('/admin');
      } else {
        navigate('/user-dashboard');
      }
    }
  }, [user, isAdmin, navigate]);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-cyan-500 animate-gradient-xy"></div>
      
      {/* Overlay Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-6xl w-full grid lg:grid-cols-2 gap-8 items-center">
          
          {/* Left Side - Hero Content */}
          <div className="text-white space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <Activity className="w-4 h-4" />
              <span className="text-sm font-medium">Splink Pro Assessment Platform</span>
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
              Unlock Your
              <span className="block bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">
                Athletic Potential
              </span>
            </h1>
            
            <p className="text-xl text-blue-100 leading-relaxed">
              Comprehensive health assessment combining nutrition analysis, 
              cognitive profiling, and physical fitness testing powered by AI.
            </p>

            {/* Features */}
            <div className="space-y-4 pt-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 text-yellow-300" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Personalized Reports</h3>
                  <p className="text-blue-100 text-sm">Get detailed insights tailored to your sport and goals</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <Activity className="w-5 h-5 text-green-300" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">AI-Powered Analysis</h3>
                  <p className="text-blue-100 text-sm">Advanced algorithms analyze your performance</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-cyan-300" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Track Progress</h3>
                  <p className="text-blue-100 text-sm">Monitor improvements over time with detailed metrics</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Sign Up Form */}
          <div className="bg-white rounded-2xl shadow-2xl backdrop-blur-lg overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-8 border-b border-gray-200">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Get Started</h2>
                <p className="text-gray-600">Create your account to begin your assessment</p>
              </div>
            </div>

            {/* Quick Access Link */}
            <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-600 border-b border-gray-200">
              <button
                onClick={() => navigate('/db-analytics')}
                className="w-full py-3 px-6 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                🚀 DB Analytics Dashboard (Direct Access)
              </button>
            </div>

            {/* Embedded Stack Auth Sign Up */}
            <div className="p-8">
              <style dangerouslySetInnerHTML={{__html: `
                .stack-scope {
                  width: 100% !important;
                  max-width: 100% !important;
                }
                .stack-scope > div {
                  width: 100% !important;
                }
                .stack-scope button {
                  width: 100% !important;
                }
                .stack-scope input {
                  color: #000000 !important;
                }
                .stack-scope input::placeholder {
                  color: #6b7280 !important;
                }
              `}} />
              <SignUp 
                fullPage={false}
                automaticRedirect={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-float"></div>
      <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/10 rounded-full blur-xl animate-float-delayed"></div>
    </div>
  );
};

export default LandingPage;
